import { useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import useEnableFastifyApi from './useEnableFastifyApi';
import { GET_BROWSE_INITIAL_INFORMATION } from '../queries';
import restRequest from './restClient';
import useAsyncCall from './useAsyncCall';

// The BFF answers 200 with `searchSailings: null` when its own sailings upstream fails, naming the
// reason in `warnings` — a partial failure the HTTP status does not carry. Without sailings the
// browse form has no ships and no dates to offer, so this is a failed load rather than an empty
// one, and raising it is what routes it to the technical-issues banner the callers already have.
function toGraphqlShape(bffResponse) {
  if (!bffResponse?.searchSailings) {
    const warning = bffResponse?.warnings?.find(({ section }) => section === 'searchSailings');
    const error = new Error(warning?.message || 'browse/initial returned no searchSailings');
    // Mirrors restClient's normalized shape: callers read graphQLErrors to branch on upstream
    // codes, and an undefined one would throw where a plain miss should just not match.
    error.graphQLErrors = [];
    error.networkError = null;
    throw error;
  }
  return {
    ships: bffResponse.ships,
    searchSailings: bffResponse.searchSailings,
    airports: bffResponse.airports,
  };
}

function useBrowseInitialRest(options) {
  return useAsyncCall(
    ({ variables: { includeSoldOutSailings, onlyActive } = {} } = {}) =>
      restRequest({
        path: '/air-api/v1/browse/initial',
        params: { includeSoldOutSailings, onlyActive },
      }).then(toGraphqlShape),
    options,
  );
}

export function useBrowseInitialLazy(options) {
  const enableFastifyApi = useEnableFastifyApi();

  const graphqlResult = useLazyQuery(GET_BROWSE_INITIAL_INFORMATION, options);
  const [restExecute, restState] = useBrowseInitialRest(options);

  if (enableFastifyApi) {
    return [restExecute, restState];
  }
  return graphqlResult;
}

export default function useBrowseInitial({ includeSoldOutSailings, onlyActive, skip } = {}) {
  const enableFastifyApi = useEnableFastifyApi();

  const graphqlResult = useQuery(GET_BROWSE_INITIAL_INFORMATION, {
    variables: { includeSoldOutSailings, onlyActive },
    skip: skip || enableFastifyApi,
  });
  const [execute, state] = useBrowseInitialRest();

  useEffect(() => {
    if (!skip && enableFastifyApi) {
      execute({ variables: { includeSoldOutSailings, onlyActive } });
    }
  }, [includeSoldOutSailings, onlyActive, skip, enableFastifyApi, execute]);

  if (!enableFastifyApi) {
    return graphqlResult;
  }

  return {
    data: state.data,
    loading: state.loading,
    error: state.error,
    refetch: () => execute({ variables: { includeSoldOutSailings, onlyActive } }),
  };
}
